import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import useStore from "../../useStore";

const MenuIllustration = () => (
  <svg width="200" height="200" viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="40" y="30" width="120" height="140" rx="8" fill="#F3DDA9" stroke="#25363E" strokeWidth="2"/>
    <rect x="50" y="50" width="100" height="20" rx="4" fill="#E9C05F"/>
    <rect x="50" y="80" width="100" height="10" rx="2" fill="#E9C05F" fillOpacity="0.7"/>
    <rect x="50" y="100" width="100" height="10" rx="2" fill="#E9C05F" fillOpacity="0.7"/>
    <rect x="50" y="120" width="100" height="10" rx="2" fill="#E9C05F" fillOpacity="0.7"/>
    <rect x="50" y="140" width="60" height="10" rx="2" fill="#E9C05F"/>
  </svg>
);

const CategoriesIllustration = () => (
  <svg width="200" height="200" viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="30" y="40" width="60" height="60" rx="15" fill="#F3DDA9" stroke="#25363E" strokeWidth="2"/>
    <rect x="110" y="40" width="60" height="60" rx="15" fill="#F3DDA9" stroke="#25363E" strokeWidth="2"/>
    <rect x="30" y="110" width="60" height="60" rx="15" fill="#F3DDA9" stroke="#25363E" strokeWidth="2"/>
    <rect x="110" y="110" width="60" height="60" rx="15" fill="#F3DDA9" stroke="#25363E" strokeWidth="2"/>
    <circle cx="60" cy="70" r="15" fill="#E9C05F"/>
    <circle cx="140" cy="70" r="15" fill="#E9C05F"/>
    <circle cx="60" cy="140" r="15" fill="#E9C05F"/>
    <circle cx="140" cy="140" r="15" fill="#E9C05F"/>
  </svg>
);

const FavoritesIllustration = () => (
  <svg width="200" height="200" viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg">
    <g transform="translate(8, -15)">
    <path d="M100 180L85.1 166.3C67 149.9 53.7 137.1 45.2 126.1C36.7 115.1 32 104.8 32 94.5C32 85.9 35.1 78.7 41.4 72.4C47.7 66.1 55.2 63 64 63C69.6 63 74.8 64.4 79.7 67.3C84.6 70.2 88.7 74.1 92 79.1C95.3 74.1 99.4 70.2 104.3 67.3C109.2 64.4 114.4 63 120 63C128.8 63 136.3 66.1 142.6 72.4C148.9 78.7 152 85.9 152 94.5C152 104.8 147.3 115.1 138.8 126.1C130.3 137.1 117 149.9 98.9 166.3L84 180H100Z" fill="#E9C05F" stroke="#25363E" strokeWidth="2"/></g> 
    <circle cx="165" cy="50" r="10" fill="#E9C05F"/>
    <circle cx="180" cy="80" r="8" fill="#E9C05F"/>
    <circle cx="40" cy="140" r="10" fill="#E9C05F"/>
    <circle cx="20" cy="120" r="8" fill="#E9C05F"/>
  </svg> );

const OrderIllustration = () => (
  <svg width="200" height="200" viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="50" y="40" width="100" height="120" rx="15" fill="#F3DDA9" stroke="#25363E" strokeWidth="2"/>
    <rect x="60" y="60" width="80" height="12" rx="2" fill="#25363E" fillOpacity="0.7"/>
    <rect x="60" y="80" width="80" height="12" rx="2" fill="#25363E" fillOpacity="0.7"/>
    <rect x="60" y="100" width="80" height="12" rx="2" fill="#25363E" fillOpacity="0.7"/>
    <rect x="60" y="130" width="40" height="20" rx="10" fill="#E9C05F" stroke="#25363E" strokeWidth="1"/>
    <line x1="100" y1="140" x2="70" y2="140" stroke="white" strokeWidth="2"/>
    <circle cx="140" cy="60" r="30" fill="#FFFFFF" stroke="#25363E" strokeWidth="2"/>
    <path d="M130 60L138 68L150 56" stroke="#25363E" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const OnboardingScreen = ({ isVisible, onComplete }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [hoverStates, setHoverStates] = useState({
    back: false,
    next: false,
    skip: false
  });
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);

  const minSwipeDistance = 50;

  useEffect(() => {
    if (isVisible) {
      const originalStyle = {
        overflow: document.body.style.overflow,
        position: document.body.style.position,
        width: document.body.style.width,
        height: document.body.style.height,
        touchAction: document.body.style.touchAction
      };

      document.body.style.overflow = "hidden";
      document.body.style.position = "fixed";
      document.body.style.width = "100%";
      document.body.style.height = "100%";
      document.body.style.touchAction = "none";

      const preventTouchMove = (e) => {
        e.preventDefault();
      };

      const preventWheel = (e) => {
        e.preventDefault();
      };

      document.addEventListener('touchmove', preventTouchMove, { passive: false });
      document.addEventListener('wheel', preventWheel, { passive: false });

      return () => {
        document.body.style.overflow = originalStyle.overflow;
        document.body.style.position = originalStyle.position;
        document.body.style.width = originalStyle.width;
        document.body.style.height = originalStyle.height;
        document.body.style.touchAction = originalStyle.touchAction;

        document.removeEventListener('touchmove', preventTouchMove);
        document.removeEventListener('wheel', preventWheel);
      };
    }
  }, [isVisible]);

  if (!isVisible) {
    return null;
  }

  const slides = [
    {
      title: "¡Bienvenido a nuestra App!",
      description: "Descubre una nueva forma de explorar nuestro menú y realizar pedidos de manera sencilla.",
      illustration: <MenuIllustration />
    },
    {
      title: "Explora Categorías",
      description: "Navega entre diferentes categorías de productos y nuestra exclusiva carta de vinos.",
      illustration: <CategoriesIllustration />
    },
    {
      title: "Añade a Favoritos",
      description: "Guarda tus platos favoritos con un clic al icono de favoritos para encontrarlos rápidamente en tu próxima visita.",
      illustration: <FavoritesIllustration />
    },
    {
      title: "Comparte tus Pedidos",
      description: "Crea pedidos y compártelos fácilmente con tus amigos o familia.",
      illustration: <OrderIllustration />
    }
  ];

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
    setIsDragging(true);
  };

  const onTouchMove = (e) => {
    if (!touchStart || !isDragging) return;
    
    const currentTouch = e.targetTouches[0].clientX;
    const diff = touchStart - currentTouch;
    
    setDragOffset(-diff);
    setTouchEnd(currentTouch);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) {
      setIsDragging(false);
      setDragOffset(0);
      return;
    }

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe && currentSlide < slides.length - 1) {
      setCurrentSlide(currentSlide + 1);
    }

    if (isRightSwipe && currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }

    setIsDragging(false);
    setDragOffset(0);
  };

  const handleNext = () => {
    if (currentSlide < slides.length - 1) {
      setCurrentSlide(currentSlide + 1);
    } else {
      completeOnboarding();
    }
  };

  const handleBack = () => {
    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  };

  const completeOnboarding = () => {
    localStorage.setItem("onboardingCompleted", "true");
    
    if (onComplete) {
      onComplete();
    }
  };

  const handleMouseEnter = (button) => {
    setHoverStates(prev => ({ ...prev, [button]: true }));
  };

  const handleMouseLeave = (button) => {
    setHoverStates(prev => ({ ...prev, [button]: false }));
  };

  const containerStyle = {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100vw",
    height: "100vh", 
    backgroundColor: "rgb(243, 221, 169)",
    zIndex: 999999,
    fontFamily: "'Alchevrola-normal', system-ui, sans-serif",
    overflow: "hidden",
    touchAction: "pan-x",
    paddingTop: 0,
    paddingBottom: 0,
    margin: 0,
    padding: "0 env(safe-area-inset-right) env(safe-area-inset-bottom) env(safe-area-inset-left)",
    overscrollBehavior: "none"
  };

  const fullscreenOverlayStyle = {
    position: "absolute", 
    top: "-50px",
    left: "-50px",
    width: `calc(100% + 100px)`,
    height: `calc(100% + 100px)`,
    backgroundColor: "rgb(243, 221, 169)",
    zIndex: -1
  };

  const slideOffset = isDragging ? dragOffset : 0;
  const translateX = -currentSlide * 100 + (slideOffset / window.innerWidth) * 100;

  return (
    <div style={containerStyle}>
      <div style={fullscreenOverlayStyle}></div>
      
      <button 
        style={{
          position: "absolute",
          top: "1.5rem",
          right: "1.5rem",
          padding: "0.5rem 1rem",
          backgroundColor: "transparent",
          border: "none",
          color: hoverStates.skip ? "#e9c05f" : "#25363e",
          fontSize: "0.9rem",
          cursor: "pointer",
          transition: "color 0.2s ease",
          fontFamily: "inherit",
          zIndex: 10
        }}
        onClick={completeOnboarding}
        onMouseEnter={() => handleMouseEnter('skip')}
        onMouseLeave={() => handleMouseLeave('skip')}
      >
        Omitir
      </button>

      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          transition: isDragging ? "none" : "transform 0.4s ease-in-out",
          transform: `translateX(${translateX}%)`,
          touchAction: "pan-x"
        }}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        {slides.map((slide, index) => (
          <div 
            key={index} 
            style={{
              position: "absolute",
              top: 0,
              left: `${index * 100}%`,
              width: "100%",
              height: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              touchAction: "pan-x"
            }}
          >
            <div 
              style={{
                position: "absolute",
                top: "20%",
                left: "50%",
                transform: "translateX(-50%)",
                width: "220px",
                height: "220px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                background: "white",
                borderRadius: "50%",
                boxShadow: "0px 0px 5px 0px #000000"
              }}
            >
              {slide.illustration}
            </div>

            <div 
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translateX(-50%)",
                width: "80%",
                maxWidth: "400px",
                textAlign: "center"
              }}
            >
              <h2 
                style={{
                  fontSize: "1.8rem",
                  fontWeight: "700",
                  marginBottom: "1rem",
                  color: "#25363e"
                }}
              >
                {slide.title}
              </h2>
              <p 
                style={{
                  fontSize: "1.1rem",
                  lineHeight: "1.6",
                  color: "#25363e"
                }}
              >
                {slide.description}
              </p>
            </div>
          </div>
        ))}
      </div>

      <div 
        style={{
          position: "absolute",
          bottom: "100px",
          left: "50%",
          transform: "translateX(-50%)",
          display: "flex",
          justifyContent: "center"
        }}
      >
        {slides.map((_, index) => (
          <div
            key={index}
            style={{
              width: "10px",
              height: "10px",
              borderRadius: "50%",
              backgroundColor: index === currentSlide ? "#E9C05F" : "#FFFFFF",
              margin: "0 6px",
              cursor: "pointer",
              border: "1px solid #25363e",
              transform: index === currentSlide ? "scale(1.2)" : "scale(1)",
              transition: "all 0.3s ease"
            }}
            onClick={() => setCurrentSlide(index)}
          />
        ))}
      </div>

      <div 
        style={{
          position: "absolute",
          bottom: "40px",
          left: "50%",
          transform: "translateX(-50%)",
          display: "flex",
          justifyContent: "center",
          width: "100%",
          maxWidth: "400px"
        }}
      >
        {currentSlide > 0 ? (
          <button 
            style={{
              width: "120px",
              padding: "0.75rem 1rem",
              borderRadius: "15px",
              border: "#e9c05f solid 1px",
              fontSize: "1rem",
              fontWeight: "600",
              cursor: "pointer",
              boxShadow: hoverStates.back ? "0px 0px 5px 0px #000000" : "0px 0px 3px 0px #000000",
              color: "#25363e",
              backgroundColor: hoverStates.back ? "#e9c05f" : "#ffffff",
              margin: "0 10px",
              transition: "all 0.2s ease",
              transform: hoverStates.back ? "translateY(-2px)" : "translateY(0)"
            }}
            onClick={handleBack}
            onMouseEnter={() => handleMouseEnter('back')}
            onMouseLeave={() => handleMouseLeave('back')}
          >
            Atrás
          </button>
        ) : (
          <div style={{ width: "120px", margin: "0 10px" }} />
        )}
        <button 
          style={{
            width: "120px",
            padding: "0.75rem 1rem",
            borderRadius: "15px",
            border: "#e9c05f solid 1px",
            fontSize: "1rem",
            fontWeight: "600",
            cursor: "pointer",
            color: "#25363e",
            backgroundColor: hoverStates.next ? "#e9c05f" : "#ffffff",
            margin: "0 10px",
            transition: "all 0.2s ease",
            transform: hoverStates.next ? "translateY(-2px)" : "translateY(0)",
            boxShadow: hoverStates.next ? "0px 0px 5px 0px #000000" : "0px 0px 3px 0px #000000"
          }}
          onClick={handleNext}
          onMouseEnter={() => handleMouseEnter('next')}
          onMouseLeave={() => handleMouseLeave('next')}
        >
          {currentSlide < slides.length - 1 ? "Siguiente" : "Comenzar"}
        </button>
      </div>
    </div>
  );
};

export default OnboardingScreen;